import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon, PencilIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useSmsFeature } from '../contexts/FeatureFlagContext';
import { CampaignLoadingSkeleton } from './Campaigns/CampaignLoadingSkeleton';
import { HelpIcon } from './HelpIcon';
import { InlineTip } from './InlineTip';
import { TooltipProvider } from './ui/tooltip';
import { getHelpContent } from '../lib/helpContent';
import type { Campaign, Template, Sequence, SequenceStep, Contact } from '../types/campaign';

interface CampaignBuilderProps {
  campaignId?: string;
  onSave?: (campaign: Campaign) => void;
  onCancel?: () => void;
  onSuccess?: (campaign: Campaign) => void;
  isModal?: boolean;
}

export const CampaignBuilder: React.FC<CampaignBuilderProps> = ({
  campaignId: campaignIdProp,
  onSave,
  onCancel,
  onSuccess,
  isModal = false
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id: campaignIdParam } = useParams<{ id: string }>();
  const isSmsEnabled = useSmsFeature();
  
  // Use campaignId from props if provided, otherwise from URL params
  const campaignId = campaignIdProp || campaignIdParam;
  const [campaign, setCampaign] = useState<Partial<Campaign>>({
    name: '',
    description: '',
    channel: 'email',
    respect_consent: true,
    respect_suppression: true,
    filters: {},
    tags: []
  });

  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [selectedSequence, setSelectedSequence] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [includeTagsText, setIncludeTagsText] = useState<string>('');
  const [excludeTagsText, setExcludeTagsText] = useState<string>('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [isWebhookOnly, setIsWebhookOnly] = useState<boolean>(false);

  // Fetch templates
  const { data: templates = [] } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: async () => {
      const response = await api.templates.list();
      const data = response.data;
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    }
  });

  // Fetch sequences
  const { data: sequences = [] } = useQuery<Sequence[]>({
    queryKey: ['sequences'],
    queryFn: async () => {
      const response = await api.sequences.list();
      const data = response.data;
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    }
  });

  // Fetch contacts
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await api.contacts.list({ limit: 1000 });
      const data = response.data;
      // Ensure we always return an array
      return Array.isArray(data) ? data : [];
    }
  });

  // Fetch public settings to check if campaign settings are locked
  const { data: publicSettings = [] } = useQuery<Array<{ key: string; value: string; description?: string }>>({
    queryKey: ['public-settings'],
    queryFn: async () => {
      try {
        const response = await api.serverAdmin.getPublicSettings();
        return response.data || [];
      } catch (error) {
        // If user doesn't have access, return empty array
        console.warn('Failed to load public settings:', error);
        return [];
      }
    }
  });

  // Check if campaign settings are locked
  const campaignSettingsLocked = React.useMemo(() => {
    const lockSetting = publicSettings.find(s => s.key === 'campaign_settings_lock');
    return lockSetting?.value?.toLowerCase() === 'true';
  }, [publicSettings]);

  // Fetch campaign if editing
  const { data: existingCampaign, isLoading: isLoadingCampaign } = useQuery<Campaign>({
    queryKey: ['campaign', campaignId],
    queryFn: async () => {
      const response = await api.campaigns.get(campaignId!);
      return response.data;
    },
    enabled: !!campaignId
  });

  // Update campaign when existing campaign loads
  useEffect(() => {
    if (existingCampaign) {
      setCampaign(existingCampaign);
      setSelectedTemplate(existingCampaign.template_id || '');
      setSelectedSequence(existingCampaign.sequence_id || '');
      
      // Load filter values
      if (existingCampaign.filters) {
        if (existingCampaign.filters.tags) {
          const tags = existingCampaign.filters.tags;
          setIncludeTagsText(tags.include?.join(', ') || '');
          setExcludeTagsText(tags.exclude?.join(', ') || '');
        }
        if (existingCampaign.filters.contact_ids) {
          // Check if it's webhook-only (empty array)
          if (Array.isArray(existingCampaign.filters.contact_ids) && existingCampaign.filters.contact_ids.length === 0) {
            setIsWebhookOnly(true);
            setSelectedContactIds([]);
          } else {
            setSelectedContactIds(existingCampaign.filters.contact_ids);
          }
        }
      }
    }
  }, [existingCampaign]);

  // Ensure consent and suppression are always enabled when locked
  useEffect(() => {
    if (campaignSettingsLocked) {
      setCampaign(prev => ({
        ...prev,
        respect_consent: true,
        respect_suppression: true
      }));
    }
  }, [campaignSettingsLocked]);

  // Validate campaign
  const validateCampaign = (): boolean => {
    const errors: string[] = [];
    
    if (!campaign.name || campaign.name.trim() === '') {
      errors.push('Campaign name is required');
    }
    
    if (!selectedTemplate && !selectedSequence) {
      errors.push('Please select either a template or a sequence');
    }
    
    if (selectedTemplate && selectedSequence) {
      errors.push('Please select either a template or a sequence, not both');
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Save campaign mutation
  const saveCampaignMutation = useMutation({
    mutationFn: async (campaignData: Partial<Campaign>) => {
      const dataToSave = {
        ...campaignData,
        // Only include template_id if it's a non-empty string
        template_id: selectedTemplate && selectedTemplate.trim() !== '' ? selectedTemplate : undefined,
        // Only include sequence_id if it's a non-empty string
        sequence_id: selectedSequence && selectedSequence.trim() !== '' ? selectedSequence : undefined,
      };
      
      if (campaignId) {
        const response = await api.campaigns.update(campaignId, dataToSave);
        return response.data;
      } else {
        const response = await api.campaigns.create(dataToSave);
        return response.data;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      
      // Show success toast
      toast.success(campaignId ? 'Campaign updated successfully' : 'Campaign created successfully');
      
      // Call callbacks
      onSave?.(data);
      onSuccess?.(data);
      
      // Redirect to campaigns list if not in modal mode
      if (!isModal && !campaignId) {
        navigate('/campaigns');
      } else if (!isModal && campaignId) {
        navigate(`/campaigns/${campaignId}`);
      }
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save campaign';
      toast.error(errorMessage);
      
      // Extract validation errors if available
      if (error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors);
      }
    },
  });

  const handleSave = () => {
    // Clear previous errors
    setValidationErrors([]);
    
    // Validate before saving
    if (!validateCampaign()) {
      toast.error('Please fix the validation errors');
      return;
    }
    
    // Build filters object from tag inputs and selected contacts
    const filters: any = {};
    
    // Handle webhook-only campaigns
    if (isWebhookOnly) {
      // Explicitly set empty array to indicate webhook-only
      filters.contact_ids = [];
    } else if (selectedContactIds.length > 0) {
      // Add selected contact IDs if any
      filters.contact_ids = selectedContactIds;
    }
    
    // Add tag filters
    if (includeTagsText.trim() || excludeTagsText.trim()) {
      filters.tags = {};
      if (includeTagsText.trim()) {
        filters.tags.include = includeTagsText.split(',').map(tag => tag.trim()).filter(tag => tag);
      }
      if (excludeTagsText.trim()) {
        filters.tags.exclude = excludeTagsText.split(',').map(tag => tag.trim()).filter(tag => tag);
      }
    }
    
    // Merge filters into campaign data
    const campaignData = {
      ...campaign,
      filters: Object.keys(filters).length > 0 ? filters : undefined
    };
    
    saveCampaignMutation.mutate(campaignData);
  };
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (!isModal) {
      navigate('/campaigns');
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setCampaign(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [key]: value
      }
    }));
  };

  const addTag = (tag: string) => {
    if (tag && !campaign.tags?.includes(tag)) {
      setCampaign(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag]
      }));
    }
  };

  const removeTag = (tag: string) => {
    setCampaign(prev => ({
      ...prev,
      tags: prev.tags?.filter(t => t !== tag)
    }));
  };

  // Show loading state while fetching campaign data
  if (campaignId && isLoadingCampaign) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="animate-pulse space-y-6">
            {/* Header skeleton */}
            <div className="space-y-3">
              <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
            
            {/* Form fields skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            </div>
            
            {/* Action buttons skeleton */}
            <div className="flex justify-end space-x-4 pt-6 border-t">
              <div className="h-10 w-24 bg-gray-200 rounded"></div>
              <div className="h-10 w-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Back Button - Only show in edit mode and not in modal */}
      {campaignId && !isModal && (
        <button
          onClick={() => navigate(`/campaigns/${campaignId}`)}
          className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Campaign Details
        </button>
      )}
      
      <TooltipProvider>
        <div className="mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {campaignId ? 'Edit Campaign' : 'Create Campaign'}
            </h2>
            {(() => {
              const workflowHelp = getHelpContent('campaignWorkflow');
              return workflowHelp ? (
                <HelpIcon
                  tooltip="Learn about campaign workflow"
                  modalTitle={workflowHelp.title}
                  modalContent={workflowHelp.content}
                  size="md"
                />
              ) : null;
            })()}
          </div>
          <p className="text-gray-600">
            {campaignId ? 'Update your campaign settings' : 'Set up a new marketing campaign'}
          </p>
        </div>
      
      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Please fix the following errors:
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <ul className="list-disc pl-5 space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Campaign Name *
            </label>
            <input
              type="text"
              value={campaign.name || ''}
              onChange={(e) => setCampaign(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter campaign name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Channel *
            </label>
            <select
              value={campaign.channel || 'email'}
              onChange={(e) => {
                const newChannel = e.target.value as 'email' | 'sms';
                setCampaign(prev => ({ ...prev, channel: newChannel }));
                // Clear selected template if it doesn't match the new channel
                if (selectedTemplate) {
                  const currentTemplate = templates.find(t => t.id === selectedTemplate);
                  if (currentTemplate && currentTemplate.template_type !== newChannel) {
                    setSelectedTemplate('');
                  }
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="email">Email</option>
              {isSmsEnabled && <option value="sms">SMS</option>}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            value={campaign.description || ''}
            onChange={(e) => setCampaign(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter campaign description"
          />
        </div>

        {/* Template/Sequence Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => {
                setSelectedTemplate(e.target.value);
                setSelectedSequence('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a template</option>
              {templates
                .filter(template => template.template_type === (campaign.channel || 'email'))
                .map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.template_type})
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sequence
            </label>
            <select
              value={selectedSequence}
              onChange={(e) => {
                setSelectedSequence(e.target.value);
                setSelectedTemplate('');
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a sequence</option>
              {sequences.map(sequence => (
                <option key={sequence.id} value={sequence.id}>
                  {sequence.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {campaign.tags?.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <input
            type="text"
            placeholder="Add a tag"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addTag(e.currentTarget.value);
                e.currentTarget.value = '';
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Contact Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Contacts
          </label>
          <select
            multiple
            value={selectedContactIds}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              setSelectedContactIds(selected);
            }}
            disabled={isWebhookOnly}
            className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px] ${
              isWebhookOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
            }`}
            size={5}
          >
            {contacts.map(contact => (
              <option key={contact.id} value={contact.id}>
                {contact.first_name || contact.last_name 
                  ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() 
                  : contact.email || contact.phone || 'Unnamed Contact'}
                {contact.email && ` (${contact.email})`}
                {contact.phone && !contact.email && ` (${contact.phone})`}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {isWebhookOnly 
              ? 'Contact selection disabled for webhook-only campaigns'
              : selectedContactIds.length > 0 
                ? `${selectedContactIds.length} contact(s) selected` 
                : 'Hold Ctrl/Cmd to select multiple contacts. Leave empty to target all contacts.'}
          </p>
        </div>

        {/* Webhook-only Option */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="webhook-only"
              checked={isWebhookOnly}
              onChange={(e) => {
                setIsWebhookOnly(e.target.checked);
                if (e.target.checked) {
                  // Clear selected contacts when enabling webhook-only
                  setSelectedContactIds([]);
                }
              }}
              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <label htmlFor="webhook-only" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Webhook-only campaign
                </label>
                <HelpIcon
                  tooltip="Learn about webhook-only campaigns"
                  modalTitle="Webhook-Only Campaigns"
                  modalContent={
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        A <strong>webhook-only campaign</strong> is designed to send messages only to contacts that are added via automation/webhooks, not to your existing contacts.
                      </p>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-900">How it works:</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          <li>When you start the campaign, it will <strong>not send to any existing contacts</strong> in your system</li>
                          <li>The campaign will wait for contacts to be added via webhooks/automation</li>
                          <li>Each time a contact is added through a webhook configured for this campaign, they will automatically receive the campaign message</li>
                          <li>This is perfect for automated follow-up campaigns triggered by external events (appointments, purchases, sign-ups, etc.)</li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <h4 className="font-semibold text-gray-900">Use cases:</h4>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                          <li>Post-appointment follow-up messages</li>
                          <li>Welcome sequences for new customers</li>
                          <li>Order confirmation and shipping updates</li>
                          <li>Event registration confirmations</li>
                        </ul>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-md">
                        <p className="text-sm text-gray-700">
                          <strong>Note:</strong> Make sure to configure your automation webhook to use this campaign's <code className="bg-white px-1 rounded">auto_enqueue_campaign_id</code> setting.
                        </p>
                      </div>
                    </div>
                  }
                  size="sm"
                  variant="subtle"
                />
              </div>
              {isWebhookOnly && (
                <p className="mt-2 text-xs text-gray-600">
                  This campaign will only send to contacts added via automation/webhooks. No existing contacts will be included when you start the campaign.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              {showFilters ? 'Hide' : 'Show'} Advanced Filters
            </button>
            {(() => {
              const filtersHelp = getHelpContent('campaignFilters');
              return filtersHelp ? (
                <HelpIcon
                  tooltip="Learn about campaign filters"
                  modalTitle={filtersHelp.title}
                  modalContent={filtersHelp.content}
                  size="sm"
                  variant="subtle"
                />
              ) : null;
            })()}
          </div>
          
          {!showFilters && (!includeTagsText && !excludeTagsText && selectedContactIds.length === 0 && !isWebhookOnly) && (
            <p className="mt-2 text-sm text-gray-500">
              No filters set - campaign will target all active contacts in your account
            </p>
          )}
          
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Include Tags
                  </label>
                  <input
                    type="text"
                    value={includeTagsText}
                    onChange={(e) => setIncludeTagsText(e.target.value)}
                    placeholder="vip, premium"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Comma-separated list of tags</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exclude Tags
                  </label>
                  <input
                    type="text"
                    value={excludeTagsText}
                    onChange={(e) => setExcludeTagsText(e.target.value)}
                    placeholder="inactive, unsubscribed"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">Comma-separated list of tags</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Campaign Settings</h3>
          
          <div className="space-y-3">
            <label className={`flex items-center ${campaignSettingsLocked ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                checked={campaign.respect_consent}
                onChange={(e) => setCampaign(prev => ({ ...prev, respect_consent: e.target.checked }))}
                disabled={campaignSettingsLocked}
                className="mr-3"
              />
              <span className={`text-sm flex items-center gap-1 ${campaignSettingsLocked ? 'text-gray-500' : 'text-gray-700'}`}>
                Respect consent preferences
                {(() => {
                  const consentHelp = getHelpContent('campaignRespectConsent');
                  return consentHelp ? (
                    <HelpIcon
                      tooltip="Learn about consent preferences"
                      modalTitle={consentHelp.title}
                      modalContent={consentHelp.content}
                      size="sm"
                      variant="subtle"
                    />
                  ) : null;
                })()}
                {campaignSettingsLocked && (
                  <span className="text-xs text-gray-400 ml-1">(Locked by server admin)</span>
                )}
              </span>
            </label>
            
            <label className={`flex items-center ${campaignSettingsLocked ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <input
                type="checkbox"
                checked={campaign.respect_suppression}
                onChange={(e) => setCampaign(prev => ({ ...prev, respect_suppression: e.target.checked }))}
                disabled={campaignSettingsLocked}
                className="mr-3"
              />
              <span className={`text-sm flex items-center gap-1 ${campaignSettingsLocked ? 'text-gray-500' : 'text-gray-700'}`}>
                Respect suppression lists
                {(() => {
                  const suppressionHelp = getHelpContent('campaignRespectSuppression');
                  return suppressionHelp ? (
                    <HelpIcon
                      tooltip="Learn about suppression lists"
                      modalTitle={suppressionHelp.title}
                      modalContent={suppressionHelp.content}
                      size="sm"
                      variant="subtle"
                    />
                  ) : null;
                })()}
                {campaignSettingsLocked && (
                  <span className="text-xs text-gray-400 ml-1">(Locked by server admin)</span>
                )}
              </span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            onClick={handleCancel}
            disabled={saveCampaignMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saveCampaignMutation.isPending}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            {saveCampaignMutation.isPending && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {saveCampaignMutation.isPending ? 'Saving...' : (campaignId ? 'Update Campaign' : 'Create Campaign')}
          </button>
        </div>
      </div>
      </TooltipProvider>
    </div>
  );
};
